import useConfirmDialog from "../../components/ConfirmDialog/useConfirmDialog";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import {
  GET_AGENT_BUYERS,
  GET_AGENT_INACTIVE_BUYERS,
  ADD_BUYER,
  UPDATE_BUYER,
  ADD_CRITERIA,
  UPDATE_CRITERIA,
  DELETE_CRITERIA,
} from "../../graphql/agentQueries";
import {
  GET_BUYER_FAVORITES,
  REMOVE_FROM_FAVORITES,
} from "../../graphql/favoriteQueries";
import { RELEASE_BUYER } from "../../graphql/fubSettingQueries";
import MatchedListingsModal from "../../components/agent/MatchedListingsModal";
import { Table, Modal, Button, Input, Form, Checkbox, Tag, Select, InputNumber } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  StopOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  HeartOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { buyerSorters, totalMatches } from "../../utils/agentTableSorters";
import { TABLE_SCROLL } from "../../constants/table";
import useIsPhone from "../../hooks/useIsPhone";
import CardList from "../../components/CardList/CardList";
import ListCard, { CardAction, initialsOf } from "../../components/CardList/ListCard";

// Cells shared by the table and the phone cards, so both show the same thing.
const criteriaTag = (record) => (
  <Tag color={record.buyer_criteria?.length > 0 ? "green" : "default"}>
    {record.buyer_criteria?.length || 0} criteria
  </Tag>
);

const matchesTag = (record) => {
  const matches = totalMatches(record);
  return <Tag color={matches > 0 ? "blue" : "default"}>{matches} matches</Tag>;
};

const buyerTags = (record) => (
  <div className="flex gap-1 flex-wrap">
    {record.pre_qualified && <Tag color="green">Pre-Qualified</Tag>}
    {record.first_time_buyer && <Tag color="purple">First Time</Tag>}
    {record.referral && <Tag color="orange">Referral</Tag>}
  </div>
);

const statusTag = (record) =>
  record.isActive === false ? <Tag color="red">Deactivated</Tag> : <Tag color="green">Active</Tag>;

const AgentBuyers = () => {
  const [activeBuyers, setActiveBuyers] = useState([]);
  const [inactiveBuyers, setInactiveBuyers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState("active"); // "all", "active", "inactive"
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [buyerToDeactivate, setBuyerToDeactivate] = useState(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [form] = Form.useForm();
  const [criteriaForm] = Form.useForm();
  const { companyId } = useParams();
  const navigate = useNavigate();

  // Buyer favorites state
  const [buyerFavorites, setBuyerFavorites] = useState([]);

  // Criteria state
  const [isCriteriaModalOpen, setIsCriteriaModalOpen] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [criteriaForBuyer, setCriteriaForBuyer] = useState(null);
  const { confirm, dialog } = useConfirmDialog();
  const [cityInput, setCityInput] = useState("");
  const [cities, setCities] = useState([]);

  // Matched listings modal state
  const [matchedListingsModal, setMatchedListingsModal] = useState({
    open: false,
    title: "",
    listings: [],
  });

  // Release to pool state
  const [isReleaseModalOpen, setIsReleaseModalOpen] = useState(false);
  const [buyerToRelease, setBuyerToRelease] = useState(null);

  const toast = useToast(4000);

  // Get current user
  const currentUser = JSON.parse(localStorage.getItem("userInfo"));

  const { loading: loadingActive, error: errorActive, data: dataActive, refetch: refetchActive } = useQuery(GET_AGENT_BUYERS, {
    variables: { userId: currentUser?.id },
    fetchPolicy: "network-only",
    skip: !currentUser?.id,
  });

  const { loading: loadingInactive, error: errorInactive, data: dataInactive, refetch: refetchInactive } = useQuery(GET_AGENT_INACTIVE_BUYERS, {
    fetchPolicy: "network-only",
  });

  // Lazy query for buyer favorites - fetched when viewing a buyer
  const [fetchBuyerFavorites, { loading: loadingFavorites }] = useLazyQuery(GET_BUYER_FAVORITES, {
    fetchPolicy: "network-only",
    onCompleted(data) {
      setBuyerFavorites(data?.getBuyerFavorites || []);
    },
  });

  const [removeFavorite, { loading: removingFavorite }] = useMutation(REMOVE_FROM_FAVORITES, {
    onCompleted() {
      toast("success", "Removed from buyer's favorites");
      // Refetch favorites for the selected buyer
      if (selectedBuyer) {
        fetchBuyerFavorites({ variables: { buyerId: selectedBuyer._id } });
      }
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const refetch = () => {
    refetchActive();
    refetchInactive();
  };

  const [addBuyer, { loading: addLoading }] = useMutation(ADD_BUYER, {
    onCompleted() {
      toast("success", "Buyer added successfully");
      refetch();
      handleCloseModal();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const [deactivateMessage, setDeactivateMessage] = useState("");

  const [updateBuyer, { loading: updateLoading }] = useMutation(UPDATE_BUYER, {
    onCompleted() {
      if (isDeleting) {
        toast("success", "Buyer deleted successfully");
        setIsDeleting(false);
      } else if (isDeactivating) {
        toast("success", deactivateMessage);
        setIsDeactivating(false);
        setBuyerToDeactivate(null);
        setDeactivateMessage("");
      } else {
        toast("success", "Buyer updated successfully");
        handleCloseModal();
      }
      refetch();
    },
    onError(e) {
      toast("error", e.message);
      setIsDeactivating(false);
      setIsDeleting(false);
    },
  });

  // Criteria mutations
  const [addCriteria, { loading: addCriteriaLoading }] = useMutation(ADD_CRITERIA, {
    onCompleted() {
      toast("success", "Search criteria added successfully");
      refetch();
      handleCloseCriteriaModal();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const [updateCriteria, { loading: updateCriteriaLoading }] = useMutation(UPDATE_CRITERIA, {
    onCompleted() {
      toast("success", "Search criteria updated successfully");
      refetch();
      handleCloseCriteriaModal();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  const [deleteCriteria] = useMutation(DELETE_CRITERIA, {
    onCompleted() {
      toast("success", "Search criteria deleted successfully");
      refetch();
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  // Release buyer to pool mutation
  const [releaseBuyer, { loading: releaseLoading }] = useMutation(RELEASE_BUYER, {
    onCompleted(data) {
      const buyer = data.releaseBuyer;
      toast("success", `${buyer.firstName} ${buyer.lastName} released back to the Buyer Pool`);
      refetch();
      setIsReleaseModalOpen(false);
      setBuyerToRelease(null);
    },
    onError(e) {
      toast("error", e.message);
    },
  });

  useEffect(() => {
    if (dataActive?.getUserBuyers) {
      setActiveBuyers(dataActive.getUserBuyers);
      // Update selectedBuyer if it exists in the new data (to reflect criteria changes in modal)
      if (selectedBuyer) {
        const updatedBuyer = dataActive.getUserBuyers.find(b => b._id === selectedBuyer._id);
        if (updatedBuyer) {
          setSelectedBuyer(updatedBuyer);
        }
      }
    }
  }, [dataActive]);

  useEffect(() => {
    if (dataInactive?.getUserUnActiveBuyers) {
      setInactiveBuyers(dataInactive.getUserUnActiveBuyers);
      // Update selectedBuyer if it exists in the inactive list (to reflect criteria changes in modal)
      if (selectedBuyer) {
        const updatedBuyer = dataInactive.getUserUnActiveBuyers.find(b => b._id === selectedBuyer._id);
        if (updatedBuyer) {
          setSelectedBuyer(updatedBuyer);
        }
      }
    }
  }, [dataInactive]);

  useEffect(() => {
    if (errorActive) {
      toast("error", errorActive.message);
    }
    if (errorInactive) {
      toast("error", errorInactive.message);
    }
  }, [errorActive, errorInactive]);

  const isPhone = useIsPhone();

  const handleSearch = (e) => {
    setSearchText(e.target.value.toLowerCase());
  };

  // Get buyers based on active filter
  const getBuyersByFilter = () => {
    if (activeFilter === "active") return activeBuyers;
    if (activeFilter === "inactive") return inactiveBuyers;
    return [...activeBuyers, ...inactiveBuyers]; // "all"
  };

  // Filter buyers based on search
  const filteredBuyers = getBuyersByFilter().filter((buyer) => {
    if (!searchText) return true;
    const searchableText = `${buyer.firstName} ${buyer.lastName} ${buyer.email} ${buyer.phone}`.toLowerCase();
    return searchableText.includes(searchText);
  });

  const handleOpenAddModal = () => {
    setSelectedBuyer(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (buyer) => {
    setSelectedBuyer(buyer);
    form.setFieldsValue({
      firstName: buyer.firstName,
      lastName: buyer.lastName,
      email: buyer.email,
      phone: buyer.phone ? formatPhoneNumber(buyer.phone.toString()) : "",
      notes: buyer.notes,
      referral: buyer.referral,
      pre_qualified: buyer.pre_qualified,
      first_time_buyer: buyer.first_time_buyer,
    });
    setIsModalOpen(true);
  };

  const handleOpenViewModal = (buyer) => {
    setSelectedBuyer(buyer);
    setIsViewModalOpen(true);
    // Fetch buyer's favorites when opening view modal
    fetchBuyerFavorites({ variables: { buyerId: buyer._id } });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBuyer(null);
    form.resetFields();
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedBuyer(null);
    setBuyerFavorites([]);
  };

  const handleOpenDeactivateModal = (buyer) => {
    setBuyerToDeactivate(buyer);
    setIsDeactivateModalOpen(true);
  };

  const handleCloseDeactivateModal = () => {
    setIsDeactivateModalOpen(false);
    setBuyerToDeactivate(null);
  };

  const handleDeleteBuyer = (buyer) =>
    confirm({
      title: `Delete ${buyer.firstName} ${buyer.lastName}?`,
      description:
        "This buyer will be permanently removed. This action cannot be undone.",
      onConfirm: () => {
        setIsDeleting(true);
        return updateBuyer({
          variables: {
            buyerInput: {
              _id: buyer._id,
              firstName: buyer.firstName,
              lastName: buyer.lastName,
              deletedAt: new Date().toISOString(),
            },
          },
        });
      },
    });

  // Release to pool handlers
  const handleOpenReleaseModal = (buyer) => {
    setBuyerToRelease(buyer);
    setIsReleaseModalOpen(true);
  };

  const handleCloseReleaseModal = () => {
    setIsReleaseModalOpen(false);
    setBuyerToRelease(null);
  };

  const handleConfirmRelease = () => {
    if (buyerToRelease) {
      releaseBuyer({
        variables: {
          buyerId: buyerToRelease._id,
        },
      });
    }
  };

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    if (!value) return "";
    let formattedNumber = "";

    // Filter non-numbers
    const regex = () => value?.replace(/[^0-9]+/g, "");

    const areaCode = () => `(${regex().slice(0, 3)})`;

    const firstSix = () => `${areaCode()} ${regex().slice(3, 6)}`;

    // Dynamic trail as user types
    const trailer = (start) => `${regex().slice(start, regex().length)}`;

    // First 3 digits
    if (regex().length <= 3) formattedNumber = regex();
    // After area code
    else if (regex().length >= 4 && regex().length <= 6)
      formattedNumber = `${areaCode()} ${trailer(3)}`;
    // After dash
    else if (regex().length >= 7)
      formattedNumber = `${firstSix()}-${trailer(6)}`;

    return formattedNumber;
  };

  const handleSubmit = (values) => {
    // Strip phone formatting before sending
    const phoneValue = values.phone ? values.phone.replace(/[^0-9]+/g, "") : "";
    const submitValues = { ...values, phone: phoneValue };

    if (selectedBuyer) {
      // Update existing buyer
      updateBuyer({
        variables: {
          buyerInput: {
            _id: selectedBuyer._id,
            ...submitValues,
          },
        },
      });
    } else {
      // Add new buyer
      addBuyer({
        variables: {
          buyerInput: {
            ...submitValues,
            user: currentUser?.id,
            company: companyId,
            isActive: true,
          },
        },
      });
    }
  };

  const handleConfirmDeactivate = () => {
    if (buyerToDeactivate) {
      setIsDeactivating(true);
      const isCurrentlyDeactivated = buyerToDeactivate.isActive === false;
      const newActiveStatus = isCurrentlyDeactivated ? true : false;
      setDeactivateMessage(isCurrentlyDeactivated ? "Buyer activated successfully" : "Buyer deactivated successfully");
      updateBuyer({
        variables: {
          buyerInput: {
            _id: buyerToDeactivate._id,
            firstName: buyerToDeactivate.firstName,
            lastName: buyerToDeactivate.lastName,
            isActive: newActiveStatus,
          },
        },
      });
      handleCloseDeactivateModal();
    }
  };

  // Criteria handlers
  const handleOpenCriteriaModal = (buyer, criteria = null) => {
    setCriteriaForBuyer(buyer);
    setSelectedCriteria(criteria);
    setCities(criteria?.cities || []);
    setCityInput("");
    if (criteria) {
      criteriaForm.setFieldsValue({
        title: criteria.title,
        minPrice: criteria.minPrice,
        maxPrice: criteria.maxPrice,
        minBeds: criteria.minBeds,
        minBath: criteria.minBath,
        minSqFt: criteria.minSqFt,
        maxSqFt: criteria.maxSqFt,
        propertyType: criteria.propertyType,
        notes: criteria.notes,
        optOut: criteria.optOut,
      });
    } else {
      criteriaForm.resetFields();
    }
    setIsCriteriaModalOpen(true);
  };

  const handleCloseCriteriaModal = () => {
    setIsCriteriaModalOpen(false);
    setSelectedCriteria(null);
    setCriteriaForBuyer(null);
    setCities([]);
    setCityInput("");
    criteriaForm.resetFields();
  };

  const handleAddCity = () => {
    if (cityInput.trim() && !cities.includes(cityInput.trim())) {
      setCities([...cities, cityInput.trim()]);
      setCityInput("");
    }
  };

  const handleRemoveCity = (cityToRemove) => {
    setCities(cities.filter((city) => city !== cityToRemove));
  };

  const handleCriteriaSubmit = (values) => {
    // Validate that at least one city is added
    if (cities.length === 0) {
      toast("error", "Please add at least one city");
      return;
    }

    const criteriaInput = {
      ...values,
      cities: cities,
      buyer: criteriaForBuyer._id,
    };

    if (selectedCriteria) {
      // Update existing criteria
      updateCriteria({
        variables: {
          criteriaInput: {
            _id: selectedCriteria._id,
            ...criteriaInput,
          },
        },
      });
    } else {
      // Add new criteria
      addCriteria({
        variables: {
          criteriaInput,
        },
      });
    }
  };

  const handleDeleteCriteria = (criteria, index) =>
    confirm({
      title: `Delete ${criteria.title || `Criteria ${index + 1}`}?`,
      description:
        "This search will be permanently deleted. This action cannot be undone.",
      onConfirm: () =>
        deleteCriteria({ variables: { criteriaId: criteria._id } }),
    });

  // Matched listings handlers
  const handleViewAllMatches = (buyer) => {
    // Extract listing data from match objects and deduplicate by listing._id
    const allListings = buyer.buyer_criteria
      .flatMap((criteria) => criteria.criteria_matches || [])
      .map((match) => match.listing) // Extract the listing from each match
      .filter((listing) => listing) // Filter out null/undefined listings
      .filter(
        (listing, index, self) =>
          index === self.findIndex((l) => l._id === listing._id)
      ); // Deduplicate by _id

    setMatchedListingsModal({
      open: true,
      title: "All Matched Listings",
      listings: allListings,
    });
  };

  const handleViewCriteriaMatches = (criteria) => {
    // Extract listing data from match objects
    const listings = (criteria.criteria_matches || [])
      .map((match) => match.listing)
      .filter((listing) => listing); // Filter out null/undefined

    setMatchedListingsModal({
      open: true,
      title: `Matched Listings for "${criteria.title}"`,
      listings: listings,
    });
  };

  const handleCloseMatchedListings = () => {
    setMatchedListingsModal({ open: false, title: "", listings: [] });
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      sorter: buyerSorters.name,
      render: (_, record) => (
        <span className="font-medium text-black">
          {record.firstName} {record.lastName}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: buyerSorters.email,
      render: (email) => email || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      sorter: buyerSorters.phone,
      render: (phone) => phone || "-",
    },
    {
      title: "Criteria",
      key: "criteria",
      sorter: buyerSorters.criteria,
      render: (_, record) => criteriaTag(record),
    },
    {
      title: "Matches",
      key: "matches",
      sorter: buyerSorters.matches,
      render: (_, record) => matchesTag(record),
    },
    {
      title: "Tags",
      key: "tags",
      render: (_, record) => buyerTags(record),
    },
    {
      title: "Status",
      key: "status",
      sorter: buyerSorters.status,
      render: (_, record) => statusTag(record),
    },
    {
      title: "Actions",
      key: "actions",
      skeleton: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleOpenViewModal(record)}
            title="View Details"
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => handleOpenEditModal(record)}
            title="Edit"
          />
          {record.isActive === false ? (
            <Button
              icon={<CheckCircleOutlined />}
              onClick={() => handleOpenDeactivateModal(record)}
              title="Activate"
              style={{ color: "#52c41a", borderColor: "#52c41a" }}
            />
          ) : (
            <Button
              icon={<StopOutlined />}
              onClick={() => handleOpenDeactivateModal(record)}
              title="Deactivate"
              style={{ color: "#666", borderColor: "#666" }}
            />
          )}
          {/* Release to Pool button - only for FUB buyers */}
          {record.fubId && (
            <Button
              icon={<UndoOutlined />}
              onClick={() => handleOpenReleaseModal(record)}
              title="Release to Buyer Pool"
              style={{ color: "#1890ff", borderColor: "#1890ff" }}
            />
          )}
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDeleteBuyer(record)}
            title="Delete"
          />
        </div>
      ),
    },
  ];

  // Two queries back this screen; either failing (with nothing rendered yet)
  // should surface as one retryable state.
  const buyersError = errorActive || errorInactive;
  const firstLoad =
    (loadingActive || loadingInactive) && !filteredBuyers.length;
  const handleRetry = () => {
    refetchActive();
    refetchInactive();
  };

  const emptyState = searchText ? (
    <EmptyState
      title="No buyers match your search"
      description={`Nothing found for "${searchText}".`}
      actionLabel="Clear search"
      onAction={() => setSearchText("")}
    />
  ) : (
    <EmptyState
      title="You haven't added any buyers yet"
      description="Buyers you add will appear here with their matched listings."
    />
  );

  // Phones: every column of a table row and the same row buttons, named.
  const buyerCard = (record) => {
    const name = `${record.firstName} ${record.lastName}`;
    return (
      <ListCard
        title={name}
        initials={initialsOf(name)}
        muted={record.isActive === false}
        subtitle={<span className="break-all">{record.email || "-"}</span>}
        status={statusTag(record)}
        actions={
          <>
            <CardAction
              label={`View details for ${name}`}
              icon={<EyeOutlined />}
              onClick={() => handleOpenViewModal(record)}
            />
            <CardAction
              label={`Edit ${name}`}
              icon={<EditOutlined />}
              onClick={() => handleOpenEditModal(record)}
            />
            {record.isActive === false ? (
              <CardAction
                label={`Activate ${name}`}
                icon={<CheckCircleOutlined />}
                onClick={() => handleOpenDeactivateModal(record)}
                style={{ color: "#52c41a", borderColor: "#52c41a" }}
              />
            ) : (
              <CardAction
                label={`Deactivate ${name}`}
                icon={<StopOutlined />}
                onClick={() => handleOpenDeactivateModal(record)}
                style={{ color: "#666", borderColor: "#666" }}
              />
            )}
            {record.fubId && (
              <CardAction
                label={`Release ${name} to Buyer Pool`}
                icon={<UndoOutlined />}
                onClick={() => handleOpenReleaseModal(record)}
                style={{ color: "#1890ff", borderColor: "#1890ff" }}
              />
            )}
            <CardAction
              label={`Delete ${name}`}
              icon={<DeleteOutlined />}
              danger
              onClick={() => handleDeleteBuyer(record)}
            />
          </>
        }
      >
        <span>{record.phone || "-"}</span>
        <div className="flex flex-wrap gap-1">
          {criteriaTag(record)}
          {matchesTag(record)}
        </div>
        {buyerTags(record)}
      </ListCard>
    );
  };

  return (
    <div id="agent-buyers">
      <Breadcrumb pageName="My Buyers" />
      <div className="pb-6">
        <Card>
          <div className="m-4">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 sm:w-auto">
                <span className="whitespace-nowrap font-bold text-lg">
                  My Buyers ({filteredBuyers.length})
                </span>
                <div className="relative w-full sm:w-72">
                  <div className="absolute top-1/2 left-1 -translate-y-1/2">
                    <svg
                      className="fill-gray-400"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.16666 3.33332C5.945 3.33332 3.33332 5.945 3.33332 9.16666C3.33332 12.3883 5.945 15 9.16666 15C12.3883 15 15 12.3883 15 9.16666C15 5.945 12.3883 3.33332 9.16666 3.33332ZM1.66666 9.16666C1.66666 5.02452 5.02452 1.66666 9.16666 1.66666C13.3088 1.66666 16.6667 5.02452 16.6667 9.16666C16.6667 13.3088 13.3088 16.6667 9.16666 16.6667C5.02452 16.6667 1.66666 13.3088 1.66666 9.16666Z"
                        fill=""
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.2857 13.2857C13.6112 12.9603 14.1388 12.9603 14.4642 13.2857L18.0892 16.9107C18.4147 17.2362 18.4147 17.7638 18.0892 18.0892C17.7638 18.4147 17.2362 18.4147 16.9107 18.0892L13.2857 14.4642C12.9603 14.1388 12.9603 13.6112 13.2857 13.2857Z"
                        fill=""
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    onChange={handleSearch}
                    placeholder="Search buyers..."
                    className="w-full rounded-md border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none"
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleOpenAddModal}
                  className="flex space-x-2 items-center whitespace-nowrap bg-primary text-white rounded-sm px-4 py-2 hover:opacity-90"
                >
                  <PlusOutlined />
                  <span>Add Buyer</span>
                </button>
              </div>
            </div>
            {/* Filter Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                onClick={() => setActiveFilter("active")}
                className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
                  activeFilter === "active"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Active Buyers
              </button>
              <button
                onClick={() => setActiveFilter("inactive")}
                className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
                  activeFilter === "inactive"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                Deactivated Buyers
              </button>
              <button
                onClick={() => setActiveFilter("all")}
                className={`px-4 py-2 font-medium text-sm border-b-2 -mb-px ${
                  activeFilter === "all"
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                All Buyers
              </button>
            </div>
          </div>
          {buyersError && !filteredBuyers.length ? (
            <ErrorState onRetry={handleRetry} />
          ) : (
            <>
              {buyersError && <ErrorBanner onRetry={handleRetry} />}
              {isPhone ? (
                <CardList
                  className="px-4 pb-4"
                  label="My Buyers"
                  loading={firstLoad}
                  items={filteredBuyers}
                  rowKey={(record) => record._id}
                  resetKey={`${searchText}|${activeFilter}`}
                  columns={columns}
                  empty={emptyState}
                  renderItem={buyerCard}
                />
              ) : (
                <Table
                  scroll={TABLE_SCROLL}
                  bordered
                  rowKey="_id"
                  pagination={{
                    position: ["bottomCenter"],
                    pageSize: 10,
                    showSizeChanger: true,
                  }}
                  {...(firstLoad
                    ? skeletonTableProps(columns)
                    : { columns, dataSource: filteredBuyers })}
                  locale={{ emptyText: emptyState }}
                />
              )}
            </>
          )}
        </Card>
      </div>

      {/* Add/Edit Buyer Modal */}
      <Modal
        title={selectedBuyer ? "Edit Buyer" : "Add New Buyer"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFieldsChange={(changedFields) => {
            if (changedFields[0]?.name[0] === "phone") {
              form.setFieldsValue({
                phone: formatPhoneNumber(changedFields[0].value),
              });
            }
          }}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: "First name is required" }]}
            >
              <Input placeholder="Enter first name" />
            </Form.Item>
            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: "Last name is required" }]}
            >
              <Input placeholder="Enter last name" />
            </Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="email" label="Email">
              <Input placeholder="Enter email" type="email" />
            </Form.Item>
            <Form.Item
              name="phone"
              label="Phone Number"
              rules={[
                {
                  transform: (value) => value?.replace(/[^0-9]+/g, ""),
                  pattern: /^[0-9]{10}$/g,
                  message: "Please enter a ten digit number.",
                },
              ]}
            >
              <Input placeholder="(XXX) XXX-XXXX" maxLength={14} />
            </Form.Item>
          </div>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Enter notes" />
          </Form.Item>
          <div className="flex gap-4">
            <Form.Item name="pre_qualified" valuePropName="checked">
              <Checkbox>Pre-Qualified</Checkbox>
            </Form.Item>
            <Form.Item name="first_time_buyer" valuePropName="checked">
              <Checkbox>First Time Buyer</Checkbox>
            </Form.Item>
            <Form.Item name="referral" valuePropName="checked">
              <Checkbox>Referral</Checkbox>
            </Form.Item>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={addLoading || updateLoading}
              className="bg-primary"
            >
              {selectedBuyer ? "Update" : "Add"} Buyer
            </Button>
          </div>
        </Form>
      </Modal>

      {/* View Buyer Details Modal */}
      <Modal
        title={`${selectedBuyer?.firstName} ${selectedBuyer?.lastName}`}
        open={isViewModalOpen}
        onCancel={handleCloseViewModal}
        footer={null}
        width={800}
      >
        {selectedBuyer && (
          <div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{selectedBuyer.email || "-"}</p>
              </div>
              <div>
                <p className="text-gray-500">Phone</p>
                <p className="font-medium">{selectedBuyer.phone || "-"}</p>
              </div>
            </div>
            {selectedBuyer.notes && (
              <div className="mb-4">
                <p className="text-gray-500">Notes</p>
                <p>{selectedBuyer.notes}</p>
              </div>
            )}
            <div className="flex gap-2 mb-4">
              {selectedBuyer.pre_qualified && (
                <Tag color="green">Pre-Qualified</Tag>
              )}
              {selectedBuyer.first_time_buyer && (
                <Tag color="purple">First Time Buyer</Tag>
              )}
              {selectedBuyer.referral && <Tag color="orange">Referral</Tag>}
            </div>

            {/* Buyer Favorites */}
            <div className="mt-4 border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-lg">
                  <HeartOutlined className="text-red-500 mr-2" />
                  Favorited Listings ({buyerFavorites.length})
                </h3>
              </div>
              {loadingFavorites ? (
                <div className="text-center py-4">
                  <AiOutlineLoading3Quarters className="animate-spin inline-block" size={24} />
                </div>
              ) : buyerFavorites.length > 0 ? (
                <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                  {buyerFavorites.map((fav) => (
                    <div
                      key={fav._id}
                      className="border rounded p-3 flex justify-between items-center hover:bg-gray-50"
                    >
                      <div
                        className="cursor-pointer flex-1"
                        onClick={() => navigate(`/company/${companyId}/agent/listings/${fav.listing._id}/view`)}
                      >
                        <div className="font-medium text-primary">
                          {fav.listing.streetAddr}
                        </div>
                        <div className="text-sm text-gray-500">
                          {fav.listing.city}, {fav.listing.state} {fav.listing.zip}
                        </div>
                        <div className="text-sm">
                          ${fav.listing.price?.toLocaleString()} | {fav.listing.beds} bed, {fav.listing.baths} bath
                        </div>
                      </div>
                      <Button
                        size="small"
                        icon={<DeleteOutlined />}
                        danger
                        loading={removingFavorite}
                        onClick={() => removeFavorite({ variables: { favoriteId: fav._id } })}
                        title="Remove from Favorites"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">
                  No favorited listings yet.
                </p>
              )}
            </div>

            {/* Buyer Criteria */}
            <div className="mt-4 border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-4">
                  <h3 className="font-semibold text-lg">Property Search Criteria</h3>
                  {selectedBuyer.buyer_criteria?.length > 0 && (
                    <span
                      className="text-primary cursor-pointer hover:underline text-sm"
                      onClick={() => handleViewAllMatches(selectedBuyer)}
                    >
                      View All Matches
                    </span>
                  )}
                </div>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => handleOpenCriteriaModal(selectedBuyer)}
                  className="bg-primary"
                >
                  Create Search
                </Button>
              </div>
              {selectedBuyer.buyer_criteria?.length > 0 ? (
                <div className="max-h-72 overflow-y-auto pr-1">
                  {selectedBuyer.buyer_criteria.map((criteria, index) => (
                    <div
                      key={criteria._id || index}
                      className="border rounded p-3 mb-3"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-primary">{criteria.title || `Criteria ${index + 1}`}</h4>
                        <div className="flex gap-1">
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleOpenCriteriaModal(selectedBuyer, criteria)}
                            title="Edit"
                          />
                          <Button
                            size="small"
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => handleDeleteCriteria(criteria, index)}
                            title="Delete"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        {criteria.minPrice && (
                          <div>
                            <span className="text-gray-500">Min Price:</span> $
                            {criteria.minPrice.toLocaleString()}
                          </div>
                        )}
                        {criteria.maxPrice && (
                          <div>
                            <span className="text-gray-500">Max Price:</span> $
                            {criteria.maxPrice.toLocaleString()}
                          </div>
                        )}
                        {criteria.minBeds && (
                          <div>
                            <span className="text-gray-500">Min Beds:</span>{" "}
                            {criteria.minBeds}
                          </div>
                        )}
                        {criteria.minBath && (
                          <div>
                            <span className="text-gray-500">Min Baths:</span>{" "}
                            {criteria.minBath}
                          </div>
                        )}
                        {criteria.minSqFt && (
                          <div>
                            <span className="text-gray-500">Min Sq Ft:</span>{" "}
                            {criteria.minSqFt.toLocaleString()}
                          </div>
                        )}
                        {criteria.maxSqFt && (
                          <div>
                            <span className="text-gray-500">Max Sq Ft:</span>{" "}
                            {criteria.maxSqFt.toLocaleString()}
                          </div>
                        )}
                        {criteria.cities?.length > 0 && (
                          <div className="col-span-2">
                            <span className="text-gray-500">Cities:</span>{" "}
                            {criteria.cities.join(", ")}
                          </div>
                        )}
                        {criteria.propertyType && (
                          <div>
                            <span className="text-gray-500">Type:</span>{" "}
                            {criteria.propertyType}
                          </div>
                        )}
                      </div>
                      {/* Criteria Matches */}
                      <div className="mt-2">
                        <div className="flex justify-between items-center">
                          <p className="text-gray-500 text-sm">
                            Matching Listings ({criteria.criteria_matches?.length || 0}):
                          </p>
                          <span
                            className="text-primary cursor-pointer hover:underline text-sm"
                            onClick={() => handleViewCriteriaMatches(criteria)}
                          >
                            View Matches ({criteria.criteria_matches?.length || 0})
                          </span>
                        </div>
                        {criteria.criteria_matches?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {criteria.criteria_matches
                              .slice(0, 3)
                              .map((match) => (
                                <Tag key={match._id} color="blue">
                                  {match.listing?.streetAddr}, {match.listing?.city}
                                </Tag>
                              ))}
                            {criteria.criteria_matches.length > 3 && (
                              <Tag>
                                +{criteria.criteria_matches.length - 3} more
                              </Tag>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No search criteria yet. Click "Create Search" to add one.</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Deactivate/Activate Confirmation Modal */}
      <Modal
        title={buyerToDeactivate?.isActive === false ? "Activate Buyer" : "Deactivate Buyer"}
        open={isDeactivateModalOpen}
        onCancel={handleCloseDeactivateModal}
        footer={null}
        width={400}
      >
        <p>
          Are you sure you want to {buyerToDeactivate?.isActive === false ? "activate" : "deactivate"}{" "}
          <strong>
            {buyerToDeactivate?.firstName} {buyerToDeactivate?.lastName}
          </strong>
          ?
        </p>
        {buyerToDeactivate?.isActive !== false && (
          <p className="text-gray-500 text-sm mt-2">
            Deactivated buyers will be moved to the Deactivated Buyers list and can be activated later.
          </p>
        )}
        <div className="mt-6 flex space-x-2">
          <button
            type="button"
            onClick={handleConfirmDeactivate}
            disabled={updateLoading}
            className="flex w-1/2 justify-center rounded bg-secondary p-3 font-medium text-white outline hover:opacity-90 disabled:bg-slate-400"
          >
            {updateLoading ? (
              <div className="animate-spin">
                <AiOutlineLoading3Quarters size={22} />
              </div>
            ) : (
              "Yes"
            )}
          </button>
          <button
            onClick={handleCloseDeactivateModal}
            disabled={updateLoading}
            className="flex w-1/2 justify-center rounded bg-primary p-3 font-medium text-white hover:opacity-90 disabled:bg-slate-400"
          >
            Cancel
          </button>
        </div>
      </Modal>

      {/* Criteria Modal */}
      <Modal
        title={selectedCriteria ? "Edit Search Criteria" : "Create Search Criteria"}
        open={isCriteriaModalOpen}
        onCancel={handleCloseCriteriaModal}
        footer={null}
        width={700}
      >
        <Form
          form={criteriaForm}
          layout="vertical"
          onFinish={handleCriteriaSubmit}
        >
          <Form.Item
            name="title"
            label="Search Name"
            rules={[{ required: true, message: "Search name is required" }]}
          >
            <Input placeholder="e.g., Downtown Condo Search" />
          </Form.Item>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Cities</label>
            <div className="flex gap-2 mb-2">
              <Input
                placeholder="Enter city name"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onPressEnter={(e) => {
                  e.preventDefault();
                  handleAddCity();
                }}
                style={{ flex: 1 }}
              />
              <Button type="primary" onClick={handleAddCity} className="bg-primary">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              {cities.map((city) => (
                <Tag
                  key={city}
                  closable
                  onClose={() => handleRemoveCity(city)}
                  color="blue"
                >
                  {city}
                </Tag>
              ))}
            </div>
          </div>

          <Form.Item name="propertyType" label="Property Type">
            <Select placeholder="Select property type" allowClear>
              <Select.Option value="sale">For Sale</Select.Option>
              <Select.Option value="rent">For Rent</Select.Option>
            </Select>
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="minBeds" label="Min Beds">
              <InputNumber min={0} max={10} placeholder="Any" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="minBath" label="Min Baths">
              <InputNumber min={0} max={10} placeholder="Any" style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="minPrice" label="Min Price">
              <InputNumber
                min={0}
                placeholder="No Min"
                style={{ width: "100%" }}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
            <Form.Item name="maxPrice" label="Max Price">
              <InputNumber
                min={0}
                placeholder="No Max"
                style={{ width: "100%" }}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="minSqFt" label="Min Sq Ft">
              <InputNumber
                min={0}
                placeholder="No Min"
                style={{ width: "100%" }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/,/g, "")}
              />
            </Form.Item>
            <Form.Item name="maxSqFt" label="Max Sq Ft">
              <InputNumber
                min={0}
                placeholder="No Max"
                style={{ width: "100%" }}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/,/g, "")}
              />
            </Form.Item>
          </div>

          <Form.Item name="notes" label="Buyer Wants & Needs Notes">
            <Input.TextArea rows={3} placeholder="Enter any specific requirements or notes" />
          </Form.Item>

          <Form.Item name="optOut" valuePropName="checked">
            <Checkbox>Hide buyer criteria from other agents</Checkbox>
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={handleCloseCriteriaModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={addCriteriaLoading || updateCriteriaLoading}
              className="bg-primary"
            >
              {selectedCriteria ? "Update" : "Create"} Search
            </Button>
          </div>
        </Form>
      </Modal>

      {dialog}

      {/* Matched Listings Modal */}
      <MatchedListingsModal
        open={matchedListingsModal.open}
        onClose={handleCloseMatchedListings}
        title={matchedListingsModal.title}
        listings={matchedListingsModal.listings}
        companyId={companyId}
      />

      {/* Release to Pool Confirmation Modal */}
      <Modal
        title="Release Buyer to Pool"
        open={isReleaseModalOpen}
        onCancel={handleCloseReleaseModal}
        footer={null}
        width={400}
      >
        <p>
          Are you sure you want to release{" "}
          <strong>
            {buyerToRelease?.firstName} {buyerToRelease?.lastName}
          </strong>{" "}
          back to the Buyer Pool?
        </p>
        <p className="text-gray-500 text-sm mt-2">
          This buyer will be available for other agents to claim.
        </p>
        <p className="text-blue-500 text-sm mt-2 italic">
          Tip: Also unassign yourself in Follow Up Boss to keep both systems aligned.
        </p>
        <div className="mt-6 flex space-x-2">
          <button
            type="button"
            onClick={handleConfirmRelease}
            disabled={releaseLoading}
            className="flex w-1/2 justify-center rounded p-3 font-medium text-white outline hover:opacity-90 disabled:bg-slate-400"
            style={{ backgroundColor: "#1890ff" }}
          >
            {releaseLoading ? (
              <div className="animate-spin">
                <AiOutlineLoading3Quarters size={22} />
              </div>
            ) : (
              "Yes, Release"
            )}
          </button>
          <button
            onClick={handleCloseReleaseModal}
            disabled={releaseLoading}
            className="flex w-1/2 justify-center rounded p-3 font-medium hover:opacity-90 disabled:bg-slate-400"
            style={{ backgroundColor: "#e5e7eb", color: "#374151" }}
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AgentBuyers;
